import { CSSProperties, KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from './Button'
import { Select } from './Select'
import { POPOVER_Z_INDEX } from './z-index'

const JUMP_PANEL_WIDTH = 72
const JUMP_PANEL_MAX_HEIGHT = 224
const JUMP_PANEL_GAP = 6
/** Pages moved by PageUp / PageDown in the page list. */
const JUMP_PAGE_STEP = 10

export interface PaginationProps {
    /** `compact` is a table footer: range, "Rows per page" select, arrow buttons and "Page X of Y". */
    variant?: 'primary' | 'secondary' | 'compact'
    totalItems: number
    pageSize?: number
    page?: number
    defaultPage?: number
    onPageChange?: (page: number) => void
    pageSizeOptions?: number[]
    onPageSizeChange?: (size: number) => void
    itemLabel?: string
    /** Disables every control (e.g. while a fetch is in flight). */
    disabled?: boolean
    /**
     * Adds a "Go to page" dropdown next to the range text (primary variant only), for jumping
     * straight to a page on a result set too long to page through with Prev/Next alone. Off by
     * default so every other table using this component is unaffected.
     */
    showPageJump?: boolean
    style?: CSSProperties
}

const getPageNumbers = (current: number, total: number): (number | '...')[] => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
    if (current <= 3) return [1, 2, 3, '...', total - 2, total - 1, total]
    if (current >= total - 2) return [1, 2, 3, '...', total - 2, total - 1, total]
    return [1, '...', current - 1, current, current + 1, '...', total]
}

const navBtnClass =
    'inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-lg bg-transparent px-2 py-1 text-sm font-medium text-foreground transition-opacity duration-150 disabled:cursor-not-allowed disabled:opacity-40'

export const Pagination = ({
    variant = 'primary',
    totalItems,
    pageSize: controlledPageSize,
    page: controlledPage,
    defaultPage = 1,
    onPageChange,
    pageSizeOptions,
    onPageSizeChange,
    itemLabel = 'invoices',
    disabled = false,
    showPageJump = false,
    style,
}: PaginationProps) => {
    const [internalPage, setInternalPage] = useState(defaultPage)
    const [internalPageSize, setInternalPageSize] = useState(controlledPageSize ?? pageSizeOptions?.[0] ?? 10)
    const [sizeOpen, setSizeOpen] = useState(false)
    const sizeRef = useRef<HTMLDivElement>(null)
    const [jumpOpen, setJumpOpen] = useState(false)
    const jumpRef = useRef<HTMLDivElement>(null)
    const jumpPanelRef = useRef<HTMLDivElement>(null)
    const jumpTriggerRef = useRef<HTMLButtonElement>(null)
    const [jumpPosition, setJumpPosition] = useState<CSSProperties | null>(null)
    // The page option holding keyboard focus while the list is open (roving tabindex).
    const [activeJumpPage, setActiveJumpPage] = useState(1)

    const currentPage = controlledPage ?? internalPage
    const currentPageSize = controlledPageSize ?? internalPageSize
    const totalPages = Math.max(1, Math.ceil(totalItems / currentPageSize))

    // Clamp for display/navigation: a controlled `page` can point past the end after a filter
    // shrinks `totalItems` (callers don't always reset to page 1), and an unclamped page renders
    // a nonsense range and wrongly disables both nav buttons.
    const safePage = Math.min(Math.max(currentPage, 1), totalPages)

    const rangeStart = totalItems === 0 ? 0 : (safePage - 1) * currentPageSize + 1
    const rangeEnd = Math.min(safePage * currentPageSize, totalItems)

    // Portaled to <body> with fixed positioning and flipped above the trigger when there isn't
    // room below: Pagination usually sits at the bottom of a card or the page, so opening
    // downward routinely pushed the panel off-screen or behind whatever followed it.
    const measureJumpPosition = useCallback(() => {
        const rect = jumpRef.current?.getBoundingClientRect()
        if (!rect) return
        const spaceBelow = window.innerHeight - rect.bottom
        const flipUp = spaceBelow < JUMP_PANEL_MAX_HEIGHT + JUMP_PANEL_GAP && rect.top > spaceBelow
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - JUMP_PANEL_WIDTH - 8))
        setJumpPosition(flipUp ? { left, bottom: window.innerHeight - rect.top + JUMP_PANEL_GAP } : { left, top: rect.bottom + JUMP_PANEL_GAP })
    }, [])

    useLayoutEffect(() => {
        if (jumpOpen) measureJumpPosition()
    }, [jumpOpen, measureJumpPosition])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (sizeRef.current && !sizeRef.current.contains(e.target as Node)) setSizeOpen(false)
            if (jumpRef.current?.contains(e.target as Node) || jumpPanelRef.current?.contains(e.target as Node)) return
            setJumpOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    useEffect(() => {
        if (!jumpOpen) return
        window.addEventListener('resize', measureJumpPosition)
        window.addEventListener('scroll', measureJumpPosition, true)
        return () => {
            window.removeEventListener('resize', measureJumpPosition)
            window.removeEventListener('scroll', measureJumpPosition, true)
        }
    }, [jumpOpen, measureJumpPosition])

    const goTo = useCallback(
        (p: number) => {
            const clamped = Math.min(Math.max(p, 1), totalPages)
            if (controlledPage === undefined) setInternalPage(clamped)
            onPageChange?.(clamped)
        },
        [totalPages, controlledPage, onPageChange]
    )

    const pageNumbers = useMemo(() => getPageNumbers(safePage, totalPages), [safePage, totalPages])
    const jumpablePages = useMemo(() => Array.from({ length: totalPages }, (_, i) => i + 1), [totalPages])

    const focusJumpOption = useCallback((page: number) => {
        const option = jumpPanelRef.current?.querySelector<HTMLElement>(`[data-page="${page}"]`)
        option?.focus()
        option?.scrollIntoView?.({ block: 'nearest' })
    }, [])

    const openJump = () => {
        setActiveJumpPage(safePage)
        setJumpOpen(true)
    }

    const closeJump = (returnFocus: boolean) => {
        setJumpOpen(false)
        if (returnFocus) jumpTriggerRef.current?.focus()
    }

    // The list is portaled to the end of <body>, so focus has to be moved into it explicitly: on
    // open it lands on the current page, and the arrow keys then move it from option to option.
    const jumpPanelMounted = jumpOpen && jumpPosition !== null
    useEffect(() => {
        if (jumpPanelMounted) focusJumpOption(activeJumpPage)
    }, [jumpPanelMounted, activeJumpPage, focusJumpOption])

    // A disabled pagination (e.g. while a fetch is in flight) cannot keep its page list open.
    useEffect(() => {
        if (disabled) setJumpOpen(false)
    }, [disabled])

    const handleJumpTriggerKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            openJump()
        } else if (e.key === 'Escape' && jumpOpen) {
            // Stop here so an enclosing Drawer or Modal does not close on the same keypress.
            e.preventDefault()
            e.stopPropagation()
            closeJump(true)
        }
    }

    const handleJumpListKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
        const moveTo = (page: number) => {
            e.preventDefault()
            setActiveJumpPage(Math.min(Math.max(page, 1), totalPages))
        }
        switch (e.key) {
            case 'ArrowDown':
                return moveTo(activeJumpPage + 1)
            case 'ArrowUp':
                return moveTo(activeJumpPage - 1)
            case 'PageDown':
                return moveTo(activeJumpPage + JUMP_PAGE_STEP)
            case 'PageUp':
                return moveTo(activeJumpPage - JUMP_PAGE_STEP)
            case 'Home':
                return moveTo(1)
            case 'End':
                return moveTo(totalPages)
            case 'Escape':
                // Stop here so an enclosing Drawer or Modal does not close on the same keypress.
                e.preventDefault()
                e.stopPropagation()
                return closeJump(true)
            case 'Tab':
                // The list lives at the end of <body>; tabbing out of it returns to the trigger
                // instead of leaving the page.
                e.preventDefault()
                return closeJump(true)
        }
    }

    const selectPageSize = (size: number) => {
        if (controlledPageSize === undefined) setInternalPageSize(size)
        onPageSizeChange?.(size)
    }

    if (variant === 'compact') {
        return (
            <div className="flex flex-wrap items-center justify-between gap-3" style={style}>
                <span className="text-sm text-foreground-muted">{totalItems === 0 ? '0 of 0' : `${rangeStart}–${rangeEnd} of ${totalItems}`}</span>
                <div className="flex items-center gap-2">
                    {pageSizeOptions && pageSizeOptions.length > 0 && (
                        <>
                            <span className="shrink-0 whitespace-nowrap text-sm text-foreground-muted">Rows per page</span>
                            <div className="w-[88px] shrink-0">
                                <Select
                                    variant="secondary"
                                    options={pageSizeOptions.map((opt) => ({ label: String(opt), value: String(opt) }))}
                                    value={String(currentPageSize)}
                                    disabled={disabled}
                                    onChange={(value) => {
                                        selectPageSize(Number(value))
                                        if (controlledPage === undefined) setInternalPage(1)
                                    }}
                                    aria-label="Rows per page"
                                    data-testid="pagination-rows-per-page"
                                />
                            </div>
                        </>
                    )}
                    <Button
                        variant="tertiary"
                        size="sm"
                        iconOnly
                        aria-label="Previous page"
                        disabled={disabled || safePage <= 1}
                        onClick={() => goTo(safePage - 1)}
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <span className="shrink-0 whitespace-nowrap text-sm text-foreground-muted">
                        Page {safePage} of {totalPages}
                    </span>
                    <Button
                        variant="tertiary"
                        size="sm"
                        iconOnly
                        aria-label="Next page"
                        disabled={disabled || safePage >= totalPages}
                        onClick={() => goTo(safePage + 1)}
                    >
                        <ChevronRight size={16} />
                    </Button>
                </div>
            </div>
        )
    }

    if (variant === 'primary') {
        return (
            <div className="flex flex-wrap items-center justify-between gap-4" style={style}>
                <div className="flex flex-wrap items-center gap-3">
                    <span className="whitespace-nowrap text-sm font-normal text-foreground-muted">
                        {rangeStart} to {rangeEnd} of {totalItems} {itemLabel}
                    </span>

                    {showPageJump && totalPages > 1 && (
                        <div ref={jumpRef} className="relative flex items-center gap-1.5 text-sm text-foreground-muted">
                            <span className="whitespace-nowrap">Go to page</span>
                            <button
                                ref={jumpTriggerRef}
                                type="button"
                                disabled={disabled}
                                onClick={() => (jumpOpen ? closeJump(false) : openJump())}
                                onKeyDown={handleJumpTriggerKeyDown}
                                aria-label={`Go to page, current page ${safePage}`}
                                aria-haspopup="listbox"
                                aria-expanded={jumpOpen}
                                className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-xl bg-surface py-1 pl-2.5 pr-2 text-sm font-medium text-foreground shadow-field disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {safePage}
                                <span className={`flex items-center transition-transform duration-150 ${jumpOpen ? 'rotate-180' : 'rotate-0'}`}>
                                    <ChevronDown size={14} strokeWidth={1.5} />
                                </span>
                            </button>

                            {jumpOpen &&
                                jumpPosition &&
                                createPortal(
                                    <div
                                        ref={jumpPanelRef}
                                        role="listbox"
                                        aria-label="Page number"
                                        onKeyDown={handleJumpListKeyDown}
                                        style={{
                                            ...jumpPosition,
                                            position: 'fixed',
                                            width: JUMP_PANEL_WIDTH,
                                            maxHeight: JUMP_PANEL_MAX_HEIGHT,
                                            zIndex: POPOVER_Z_INDEX,
                                        }}
                                        className="flex flex-col overflow-y-auto rounded-2xl bg-surface p-1 shadow-overlay"
                                    >
                                        {jumpablePages.map((p) => (
                                            <button
                                                key={p}
                                                type="button"
                                                role="option"
                                                aria-selected={p === safePage}
                                                data-page={p}
                                                tabIndex={p === activeJumpPage ? 0 : -1}
                                                onClick={() => {
                                                    goTo(p)
                                                    closeJump(true)
                                                }}
                                                className={`flex min-h-8 w-full cursor-pointer items-center justify-center rounded-[20px] px-3 text-sm font-medium text-foreground ${
                                                    p === safePage ? 'bg-default' : 'bg-transparent hover:bg-default focus-visible:bg-default'
                                                }`}
                                            >
                                                {p}
                                            </button>
                                        ))}
                                    </div>,
                                    document.body
                                )}
                        </div>
                    )}

                    {pageSizeOptions && pageSizeOptions.length > 0 && (
                        <div className="flex items-center gap-1.5 text-sm text-foreground-muted">
                            <span className="whitespace-nowrap">Rows per page</span>
                            <div className="w-[88px] shrink-0">
                                <Select
                                    variant="secondary"
                                    aria-label="Rows per page"
                                    options={pageSizeOptions.map((opt) => ({ label: String(opt), value: String(opt) }))}
                                    value={String(currentPageSize)}
                                    disabled={disabled}
                                    onChange={(value) => {
                                        selectPageSize(Number(value))
                                        if (controlledPage === undefined) setInternalPage(1)
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <button type="button" onClick={() => goTo(safePage - 1)} disabled={safePage <= 1} className={navBtnClass}>
                        <ChevronLeft size={16} strokeWidth={1.5} /> Prev
                    </button>
                    <button type="button" onClick={() => goTo(safePage + 1)} disabled={safePage >= totalPages} className={navBtnClass}>
                        Next <ChevronRight size={16} strokeWidth={1.5} />
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-wrap items-center justify-between gap-4" style={style}>
            {/* Left: Showing X-Y of Z */}
            <div className="flex items-center gap-2 text-sm font-normal text-foreground-muted">
                <span>Showing</span>

                {pageSizeOptions && pageSizeOptions.length > 0 ? (
                    <div ref={sizeRef} className="relative">
                        <button
                            type="button"
                            onClick={() => setSizeOpen((v) => !v)}
                            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl bg-surface py-[7px] pl-3.5 pr-2.5 text-sm font-medium text-foreground shadow-field"
                        >
                            1-{currentPageSize}
                            <span className={`flex items-center transition-transform duration-150 ${sizeOpen ? 'rotate-180' : 'rotate-0'}`}>
                                <ChevronDown size={16} strokeWidth={1.5} />
                            </span>
                        </button>

                        {sizeOpen && (
                            <div className="absolute left-0 top-[calc(100%+6px)] z-[100] flex min-w-[100px] flex-col rounded-3xl bg-surface p-1 shadow-overlay">
                                {pageSizeOptions.map((opt) => (
                                    <button
                                        key={opt}
                                        type="button"
                                        onClick={() => {
                                            selectPageSize(opt)
                                            goTo(1)
                                            setSizeOpen(false)
                                        }}
                                        className={`flex min-h-9 w-full cursor-pointer items-center justify-between rounded-[20px] px-3 py-1.5 text-left text-sm font-medium text-foreground ${
                                            opt === currentPageSize ? 'bg-default' : 'bg-transparent hover:bg-default'
                                        }`}
                                    >
                                        1-{opt}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <span className="inline-flex items-center whitespace-nowrap rounded-xl bg-surface px-3.5 py-[7px] text-sm font-medium text-foreground shadow-field">
                        1-{currentPageSize}
                    </span>
                )}

                <span>of {totalItems} results</span>
            </div>

            {/* Right: page numbers */}
            <div className="flex items-center gap-0.5">
                <button type="button" onClick={() => goTo(safePage - 1)} disabled={safePage <= 1} className={navBtnClass}>
                    <ChevronLeft size={16} strokeWidth={1.5} /> Previous
                </button>

                {pageNumbers.map((p, i) =>
                    p === '...' ? (
                        <span key={`e${i}`} className="min-w-8 px-1 text-center text-sm text-foreground-muted">
                            ...
                        </span>
                    ) : (
                        <button
                            key={p}
                            type="button"
                            onClick={() => goTo(p as number)}
                            className={`inline-flex size-8 cursor-pointer items-center justify-center rounded-full p-0 text-sm text-foreground ${
                                p === safePage ? 'bg-default font-semibold' : 'bg-transparent font-normal hover:bg-default'
                            }`}
                        >
                            {p}
                        </button>
                    )
                )}

                <button type="button" onClick={() => goTo(safePage + 1)} disabled={safePage >= totalPages} className={navBtnClass}>
                    Next <ChevronRight size={16} strokeWidth={1.5} />
                </button>
            </div>
        </div>
    )
}
